import { animated } from "@react-spring/web";
import { keyBy } from "lodash-es";
import { useTranslation } from "react-i18next";

import Icon from "Atoms/Icons";

import {
  ResponsiveTitleBar,
  ResponsivePrimaryAction,
  LinkButton,
  Price,
} from "../../Atoms";
import { AccordionDropdown } from "../BillSplit/Atoms";
import {
  UnclaimedOrderItem,
  ClaimBox,
  ClaimableOrderItemContent,
} from "../BillSplit/ClaimedContent";
import { MissingCustomer } from "../BillSplit/MissingCustomer";
import { useCreateOrderItemClaim } from "../BillSplit/OrderItemClaims/queries";
import { useLoadedBillSplit } from "../BillSplit/queries";
import { BillSplit, OrderItemClaim } from "../BillSplit/schema";
import {
  getClaimedOrderItemsIn,
  getPaidAtTillAmount,
  getBillSplitPaidOrderItems,
  getUnclaimedOrderItems,
} from "../BillSplit/selectors";
import { useOrderItemsTransitions } from "../BillSplit/useOrderItemsTransitions";
import { bustleBeeForCustomer } from "../Customer/bustleBees";
import { useLoadedCustomer } from "../Customer/queries";
import { Customer } from "../Customer/schema";
import { useLoadedCustomerTab } from "./queries";
import { OrderItem } from "./schema";
import { getOrderItemTotals } from "./selectors";

export const BillReview = ({}: {}) => {
  const { t } = useTranslation("onsite");
  const { data: customerTab } = useLoadedCustomerTab();
  const { data: billSplit } = useLoadedBillSplit();
  const { data: customer } = useLoadedCustomer();

  const createCreateOrderItemClaim = useCreateOrderItemClaim(billSplit);
  const claimItem = (orderItemUuid: string) => async () => {
    createCreateOrderItemClaim.mutate(orderItemUuid);
  };

  const orderItems = customerTab.orders.flatMap((o) => o.orderItems);
  const orderItemClaims = keyBy(billSplit.orderItemClaims, "orderItemId");
  const customers = keyBy(billSplit.customers, "uuid");
  const unclaimedOrderItems = getUnclaimedOrderItems(billSplit, customerTab);
  const getClaimedOrderItemsFor = getClaimedOrderItemsIn(
    billSplit,
    customerTab,
  );

  const yourOrderItems = getClaimedOrderItemsFor(customer);
  const claimWithCustomer = (claim: OrderItemClaim) => {
    return {
      ...claim,
      customer: customers[claim.customerId] || MissingCustomer,
    };
  };
  const completedSessionIds = billSplit.customerSessions
    .filter((cs) => !!cs.completedAt)
    .map((cs) => cs.uuid);

  const paidOrderItems = getBillSplitPaidOrderItems(billSplit, customerTab);

  const unpaidClaimedOrderItems = orderItems.filter(
    (oi) =>
      !!orderItemClaims[oi.uuid] &&
      !completedSessionIds.includes(
        orderItemClaims[oi.uuid]?.customerSessionId,
      ),
  );

  const unclaimedOrderItemsTransitions =
    useOrderItemsTransitions(unclaimedOrderItems);
  const paidOrderItemsTransitions = useOrderItemsTransitions(paidOrderItems);
  const unpaidClaimedOrderItemTransitions = useOrderItemsTransitions(
    unpaidClaimedOrderItems,
  );

  const unclaimedOrderItemsTotal = getOrderItemTotals(unclaimedOrderItems);
  const unpaidClaimedOrderItemsTotal = getOrderItemTotals(
    unpaidClaimedOrderItems,
  );
  const paidOrderItemsTotal = getOrderItemTotals(paidOrderItems);
  const paidAtTillAmount = getPaidAtTillAmount(billSplit, customerTab);

  return (
    <div className="mx-auto max-w-screen-sm space-y-32">
      <ResponsiveTitleBar>{t("billSplit.yourBill")}</ResponsiveTitleBar>
      <AccordionDropdown
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.unselected", { count: unclaimedOrderItems.length })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={unclaimedOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8">
          {unclaimedOrderItemsTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <UnclaimedOrderItem
                orderItem={oi}
                claimItem={claimItem(oi.uuid)}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <AccordionDropdown
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.selected", {
                count: unpaidClaimedOrderItems.length,
              })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={unpaidClaimedOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8 overflow-hidden">
          {unpaidClaimedOrderItemTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <TheirClaimedOrderItem
                orderItem={oi}
                claim={claimWithCustomer(orderItemClaims[oi.uuid])}
                billSplit={billSplit}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <AccordionDropdown
        isDefaultClosed
        title={
          <>
            <h3 className="text-xl font-bold">
              {t("billSplit.paid", { count: paidOrderItems.length })}
            </h3>
            <div className="font-bold">
              {t("billSplit.total")}{" "}
              <Price showCurrency value={paidOrderItemsTotal} />
            </div>
          </>
        }
      >
        <div className="space-y-8">
          {paidOrderItemsTransitions((style, oi) => (
            <animated.div style={{ ...style, overflow: "hidden" }}>
              <TheirClaimedOrderItem
                orderItem={oi}
                billSplit={billSplit}
                claim={claimWithCustomer(orderItemClaims[oi.uuid])}
              />
            </animated.div>
          ))}
        </div>
      </AccordionDropdown>
      <h3 className="text-xl font-bold">
        {t("billSplit.paidAtTillAmount")}{" "}
        <Price showCurrency value={paidAtTillAmount} />
      </h3>
      <ResponsivePrimaryAction>
        <LinkButton primary to={`../ownerReview`}>
          {t("common.ok")}
        </LinkButton>
      </ResponsivePrimaryAction>
    </div>
  );
};

const OldUnclaimedOrderItem = ({
  orderItem,
  claimItem,
}: {
  orderItem: OrderItem;
  claimItem: () => void;
}) => {
  const { t } = useTranslation("onsite");
  return (
    <div
      onClick={claimItem}
      className="flex flex-wrap items-center gap-8 rounded-venue-brand border-2 border-venue-tertiary bg-venue-tertiary p-12 text-base font-medium shadow-venue-brand-style hover:border-venue-primary"
    >
      <ClaimableOrderItemContent orderItem={orderItem} />
    </div>
  );
};

export const YourClaimedOrderItem = ({
  orderItem,
  deleteOrderItemClaim,
}: {
  orderItem: OrderItem;
  deleteOrderItemClaim: () => void;
}) => {
  return (
    <div className="flex flex-wrap items-center justify-between gap-8 rounded-venue-brand border-2 border-venue-tertiary bg-venue-tertiary p-12 text-base font-medium shadow-venue-brand-style hover:border-venue-primary">
      <ClaimableOrderItemContent orderItem={orderItem} />
      <div>
        <div className="text-venue-highlight">
          <button type="button" onClick={deleteOrderItemClaim}>
            <Icon icon="delete" />
          </button>
        </div>
      </div>
    </div>
  );
};
const TheirClaimedOrderItem = ({
  billSplit,
  orderItem,
  disabled,
  claim,
}: {
  billSplit: BillSplit;
  orderItem: OrderItem;
  disabled?: boolean;
  claim: OrderItemClaim & { customer: Customer };
}) => {
  const customerSession = billSplit.customerSessions.find(
    (cs) => cs.uuid === claim.customerSessionId,
  );
  const bustleBee = bustleBeeForCustomer(claim.customer);
  return (
    <ClaimBox theirClaim disabled>
      <ClaimableOrderItemContent orderItem={orderItem} />
      {bustleBee && (
        <div className="flex items-center gap-8">
          {claim.customer.nickname}
          <div className="h-32 w-32 rounded-full bg-white">
            <img src={bustleBee.img_96} alt={bustleBee.alt} />
          </div>
        </div>
      )}
    </ClaimBox>
  );
};
